export const faq = {
  seo: {
    title: 'Frequently asked questions about Bistroo | Bistroo',
    description: 'Answers to the most frequently asked questions about Bistroo: pricing, onboarding, hardware, payouts and more.',
  },
  hero: {
    heading: {
      1: 'The',
      2: 'most frequently asked questions',
      3: 'on one clear page',
    },
    description: {
      1: 'Have a question? There is a good chance you will find the answer below. We have listed the most frequently asked questions so you can quickly and easily find what you need on your own.',
      2: "Can't find your answer? Then our customer service team is of course ready to help.",
    },
  },
  questions: {
    1: {
      question: 'What is Bistroo and how does it work?',
      answer:
        'Bistroo is an innovative software company focused on the digitalization of the hospitality industry. The company offers user-friendly tools that allow restaurants, cafes and other hospitality businesses to improve and independently manage their online presence.',
      bullet_title: 'Bistroo provides, among other things:',
      bullets: {
        1: 'Websites and webshops for hospitality businesses, fully customizable to your preferences.',
        2: 'QR modules that allow customers to easily order and pay via their mobile phones on-site.',
        3: 'A management system where you can configure everything yourself: menus, opening hours, promotions, delivery settings, and more.',
      },
      how: 'How does it work?',
      list: {
        1: 'You create an account with Bistroo and set up your digital environment. You can add your menu, adjust settings, link your payment method and decide for yourself what your online restaurant looks like.',
        2: 'Once everything is ready, your website, webshop or QR module goes live. Customers can find you, place orders or order on the spot via QR.',
        3: 'You maintain full control over your store. Everything is adjustable by you, but if you need help, the Bistroo support team is ready to support you optimally.',
      },
    },
    2: {
      question: 'Is Bistroo free?',
      answer:
        'Yes, Bistroo offers a free entry-level package. This means that as a hospitality entrepreneur you can use the modules free of charge, without monthly subscription fees or startup costs.',
      how: 'How does the free package work?',
      bullets: {
        1: 'Free startup: You can immediately start setting up your webshop and website — no startup costs! Only for the QR module do we pass on the cost price of the stickers.',
        2: 'No subscription fees: we do not charge fixed subscription fees, we only ask for a volume-based fee percentage on successful orders.',
        3: 'Self-setup: You have access to all basic features to fully configure your digital environment.',
        4: 'Support available: Even as a free user you can count on support from our team.',
        5: 'Easy switching: you can switch to a different package every month.',
      },
      why: 'Why this model?',
      why_answer:
        'Bistroo wants to give hospitality entrepreneurs the freedom to start digitalizing without financial barriers. You only pay a volume-based fee percentage once you actually achieve results. This makes it an accessible and fair solution for both new and established hospitality businesses.',
    },
    3: {
      question: 'Can I try Bistroo first?',
      answer:
        'Yes, definitely! All Bistroo packages can be tried for free for 30 days. This means you can use all functionalities of the package that suits your hospitality business without obligation, whether you choose a website, webshop or QR ordering module.',
      what: 'What does the trial period include?',
      bullets: {
        1: '30 days of free access to all features of the chosen package.',
        2: 'Complete freedom to set up and test your digital environment.',
        3: 'Support from the support team throughout the entire trial period.',
        4: 'Hardware on loan (if applicable) during the trial period.',
      },
    },
    4: {
      question: 'How do you handle extra requests?',
      answer: {
        1: 'At Bistroo we take extra requests seriously. Our support team processes all incoming requests daily, so you are helped quickly and your online environment stays up-to-date.',
        2: 'In addition, we hold a weekly meeting where we discuss all incoming feedback and requests. During this meeting we assess which suggestions can be included in future feature updates.',
      },
    },
    5: {
      question: 'How do payouts work at Bistroo?',
      answer: {
        1: 'At Bistroo, payouts are simple, flexible and fully automated.',
        2: "In short: You stay in control of your cash flows, and we ensure a smooth and clear process. It's that easy.",
      },
      bullets: {
        1: 'Link your payment provider to our system.',
        2: 'Choose your own payout timing: decide whether you want to be paid daily, weekly or monthly — exactly as it suits your business.',
        3: 'Automatic invoicing: An invoice is automatically generated for every payout, so your administration is always correct and up-to-date.',
      },
    },
    6: {
      question: 'Why should I choose you? What makes you different?',
      answer: {
        1: 'With Bistroo you are fully the entrepreneur again. Our solution gives you, as the seller, control over your online store. You truly become the process owner, with complete control over your online restaurant. We offer all the tools and support you need to make your restaurant successful online.',
        2: 'In the Manager, our user-friendly back-office dashboard, you easily manage everything in real time: from menus, prices, discounts and promotions to (re)payments and customer data. Everything is clear and intuitive, so you can focus on what really counts.',
        3: 'Bistroo also offers unique pricing: you choose the cost structure that fits your business. No surprises, no fine print.',
        4: 'Bistroo gives you the freedom to do business online your way.',
      },
      bullets: {
        1: '30-day free trial',
        2: 'Cancel monthly',
        3: 'No hidden contracts, just clear and transparent',
      },
    },
    7: {
      question: 'How does the administration work?',
      answer: {
        1: 'At Bistroo, the administration is designed to be as simple as possible. For every payout you automatically receive a clear turnover invoice with all necessary details. This allows you to record it immediately without any hassle.',
        2: 'In short: your administration is always complete, organized and ready for your bookkeeping without any extra manual steps.',
      },
    },
    8: {
      question: 'How long does onboarding take?',
      answer:
        "With Bistroo you'll be online fast: your online restaurant is fully set up and ready for use within 48 hours. This means you can start ordering and selling almost immediately!",
    },
    9: {
      question: 'Which payment methods do you support?',
      answer:
        "Bistroo works with leading payment providers Stripe and Mollie. Through these partners we support virtually all common (and less common) payment methods, both in the Netherlands and Belgium. Think iDEAL, Bancontact, Apple Pay, Google Pay, Credit Card and many more. You have full control over the payment methods you want to offer — simply turn them on or off via your payment provider's portal.",
    },
    10: {
      question: 'How do we receive orders in the restaurant?',
      answer: {
        1: 'At Bistroo you have several flexible options for receiving orders, tailored to what best suits your restaurant. By default, orders arrive directly in your email inbox. Notifications also appear immediately in our Manager app or manager portal, and you can enable SMS notifications.',
        2: 'Want to make it even easier? We also supply hardware such as receipt printers and handhelds. Moreover, in many cases we can connect your existing printers in the restaurant, so you can get started right away without extra equipment.',
        3: 'In addition, we often offer integrations with POS systems, so that orders are seamlessly integrated into your current workflow. This saves time and minimizes errors during processing.',
        4: 'In short: with Bistroo you choose how you receive your orders, completely tailored to your business operations.',
      },
    },
    11: {
      question: 'How can I get started? What do I need?',
      answer:
        "You can start right away — nothing is required at all. Want to get going? Simply create a free account on our website, you can do it immediately. If you need specific hardware, you can buy it from us, but it's absolutely not a requirement.",
    },
    12: {
      question: 'Do you charge startup fees?',
      answer:
        'We do NOT charge startup fees. Only for the QR module do we pass on the cost price of the QR stickers.',
    },
    '15': {
      question: 'What makes Bistroo unique?',
      answer:
        "With Bistroo software you are fully an entrepreneur again. Bistroo's solution gives you, as the seller, control over your online store. You truly become the process owner, with complete control over your online restaurant via the Bistroo dashboard. We offer all the tools and support you need to make your restaurant successful online. Furthermore, you can arrange everything online via Bistroo's self-onboarding. Of course, the Bistroo team also visits on-site if needed.",
    },
    '16': {
      question: 'Why is Bistroo a better alternative than ordering services?',
      answer:
        "Bistroo distinguishes itself by focusing on the entrepreneur's profitability. Where traditional ordering services charge volume-based fees up to 30% and keep customer data for themselves, Bistroo is a software provider with a fixed and transparently low rate. The most important difference is that with Bistroo, the entrepreneur remains the owner of the customer data, which is essential for marketing and customer loyalty.",
    },
    '17': {
      question: 'What products does Bistroo provide?',
      answer:
        'Bistroo provides a complete e-commerce package for the hospitality industry, all manageable from the Manager, with the following products: BistrooWebshop: A webshop for hospitality businesses for pickup and delivery orders. BistrooPage: Website + webshop for hospitality businesses via Bistroo software. BistrooQR: QR module allowing customers to order and pay on-site. BistrooManager: A management system for menu and settings management. BistrooKiosk: An ordering kiosk allowing customers to make their choice on-site. BistrooPOS: A POS system with all functionalities for takeaway restaurants.',
    },
    '18': {
      question: 'Can I use Bistroo software if I am also on other ordering services?',
      answer:
        'Yes, Bistroo software works excellently alongside external ordering services. We help you build your own direct ordering channel via your own webshop, so you become less dependent on an ordering service.',
    },
    '19': {
      question: 'Does Bistroo have a delivery service?',
      answer:
        'No, Bistroo does not offer a delivery service itself like ordering services do. However, you can use your own delivery drivers and manage them entirely via the Bistroo software.',
    },
    '20': {
      question: 'Can I participate without my own delivery drivers?',
      answer: 'Yes, you can start with Bistroo software using only pickup or on-site QR ordering.',
    },
    '21': {
      question: 'What results do restaurants typically achieve with Bistroo software?',
      answer:
        'Our Bistroo case studies show that restaurants actively using their own direct channel see their average margin per order increase by 15% to 25% compared to ordering services or phone orders. We also see that order values for Bistroo QR on-site orders are often 20% higher thanks to smart upsell features.',
    },
    '22': {
      question: 'How does Bistroo help build a loyal customer base?',
      answer:
        "With Bistroo you own the data. You can send customers who haven't ordered in a while a personal discount code. Case studies show that restaurants using Bistroo software exponentially increase their repeat visits.",
    },
    '23': {
      question: 'Can Bistroo software handle high volumes during peak times?',
      answer:
        'Absolutely. We have various cases of busy takeaway restaurants and large terraces that rely entirely on the stable Bistroo software during peak moments. The direct connection within the Bistroo system ensures the error margin in the kitchen is reduced to almost zero. Smart time slots ensure a good distribution of orders or products during rush hour, which is difficult to manage via other systems or by phone.',
    },
    '24': {
      question: 'How do entrepreneurs experience the switch to Bistroo software?',
      answer:
        "Entrepreneurs often mention the 'freedom' of Bistroo compared to ordering services as the biggest advantage. Additionally, restaurants see a massive efficiency gain compared to phone orders. The ability to directly adjust your menu or launch a promotion via the Bistroo Manager is also seen as highly valuable.",
    },
    '26': {
      question:
        'Is Bistroo software suitable for franchise chains or companies with multiple locations?',
      answer:
        'Yes, the Bistroo Manager is designed for scalability. From one central account, entrepreneurs can manage multiple branches with Bistroo software. Our split payment structure also makes the payment flow very smooth without extra administration.',
    },
    '27': {
      question:
        "Does Bistroo also offer solutions for 'dark kitchens' or delivery restaurants without seating?",
      answer:
        'Absolutely. For dark kitchens, Bistroo offers a streamlined workflow where orders arrive directly on the handheld, ensuring independence from external ordering services through the Bistroo software.',
    },
    '28': {
      question: 'Which POS systems does Bistroo software work with?',
      answer:
        'Bistroo software works with Lightspeed, Winston Pos, Untill and Cashdesk. Additionally, Bistroo provides its own POS system that is fully integrated with our other products.',
    },
    '29': {
      question: 'Can I connect my bookkeeping to the Bistroo system?',
      answer:
        'Bistroo facilitates efficient administration by offering exports compatible with most accounting packages. Thanks to the partnership with payment provider Stripe, financial flows are clearly reported, significantly speeding up processing in packages such as Exact Online, Twinfield or Yuki.',
    },
    '30': {
      question: 'Do you create a website for me with Bistroo software?',
      answer:
        'Yes, with the BistrooPage product we create a complete website for you that is optimized for conversion.',
    },
    '31': {
      question: 'I already have a website; can I still use Bistroo software?',
      answer:
        'Yes, via the Bistroo Button you can easily link your Bistroo webshop to your existing site.',
    },
    '32': {
      question: 'I already have a domain; can I link it to Bistroo software?',
      answer:
        'Yes, you can link your domain to a new BistrooWebshop or BistrooPage via a transfer code that the Bistroo team will process for you.',
    },
    '33': {
      question: 'How does Bistroo software help solve staff shortages?',
      answer:
        'By using the Bistroo QR module or the BistrooKiosk, guests can order independently. This reduces pressure on staff, allowing them to get more done with the help of Bistroo software.',
    },
    '34': {
      question: 'How does Bistroo help my restaurant rank higher on Google?',
      answer:
        'With the BistrooPage product we technically optimize the website (SEO). Because the menu is published on the site via Bistroo as HTML text, Google can index the dishes.',
    },
    '35': {
      question: 'Can I try Bistroo software for free?',
      answer:
        'Yes, all Bistroo packages can be tried for free for 30 days without obligation and without startup costs!',
    },
    '36': {
      question: 'What are the costs of Bistroo software?',
      answer:
        'Bistroo charges a fixed monthly amount for subscriptions, or offers a free module where you only pay a volume fee per order. Additional costs such as Bistroo hardware are calculated separately. No startup costs! Check the website for specific pricing for the products.',
    },
    '37': {
      question: 'Can I cancel monthly with Bistroo?',
      answer: 'Our subscriptions can always be canceled on a monthly basis.',
    },
    '38': {
      question: 'Do I get a discount if I pay a year in advance with Bistroo?',
      answer:
        'Yes, if you wish to pay a year in advance you receive a discount on the subscription at Bistroo.',
    },
    '39': {
      question: 'What do I need to get started with Bistroo?',
      answer:
        'You can start with Bistroo immediately — all you need is an account on the Bistroo website.',
    },
    '40': {
      question: 'What do I need to start with Bistroo software?',
      answer:
        'A mobile device, internet connection and your restaurant details are enough to get started with Bistroo software.',
    },
    '41': {
      question: 'How do I sign up with Bistroo?',
      answer:
        'You can sign up via the Bistroo website or via WhatsApp at 085-0250205. You can create your own account at merchant.bistroo.nl/register.',
    },
    '42': {
      question: 'Do I get help setting up my Bistroo account?',
      answer: 'Yes, Bistroo offers personal onboarding, both on-site and remotely.',
    },
    '43': {
      question: 'Do I get help setting up my online menu in Bistroo software?',
      answer:
        'Absolutely! The Bistroo support team helps with setting up your menu and explains how to make changes.',
    },
    '44': {
      question: 'What if I have no technical knowledge; can I still use Bistroo software?',
      answer:
        'Absolutely! Bistroo software is designed to be very user-friendly, and we guide you step by step.',
    },
    '45': {
      question: 'What hardware does Bistroo provide?',
      answer:
        'Bistroo provides a Sunmi handheld device. We can supply laptops on request, USB/WiFi/Internet receipt printers from Epson and a cloud printer from Sunmi. We also provide the hardware for our POS system, consisting of: a PIN terminal, a Sunmi display, a Sunmi cloud printer and a cash drawer. Other hardware is available on request — our software runs on everything. For our ordering kiosks, various models are available for purchase, ranging from an entry-level model to fully featured double-sided kiosks.',
    },
    '46': {
      question: 'How does the Bistroo handheld work?',
      answer:
        'The Bistroo Sunmi handheld is easy to use for order management and receipt printing with Bistroo software.',
    },
    '47': {
      question: 'Does Bistroo software work on my existing hardware?',
      answer: 'Yes, Bistroo software runs on any modern device with a browser.',
    },
    '48': {
      question: 'What should I do if Bistroo software has no internet connection?',
      answer:
        'Use a hotspot or contact Bistroo customer service to activate a SIM card.',
    },
    '49': {
      question: 'How do I switch WiFi networks on the Bistroo handheld?',
      answer:
        'Via the settings of the Bistroo device, you can easily switch networks for a stable connection.',
    },
    '50': {
      question: 'Is there a SIM card in the Bistroo equipment?',
      answer: 'Yes, the Bistroo equipment contains a SIM card that can be activated on request.',
    },
    '51': {
      question: 'The handheld printer is not working — what should I do?',
      answer: 'Check the receipt roll and the connection, or contact Bistroo support.',
    },
    '52': {
      question: 'How do I replace the receipt roll in the Bistroo equipment?',
      answer: 'Place the roll with the paper side facing forward in the Bistroo device and test a print.',
    },
    '53': {
      question: 'What size receipt roll should I use for the Bistroo handheld?',
      answer: 'The Sunmi handheld from Bistroo accepts 57x40x12 thermal white receipt rolls.',
    },
    '54': {
      question: 'Are receipt rolls provided by Bistroo?',
      answer:
        'You receive one roll as standard upon delivery from Bistroo. Extra rolls can be ordered via 123inkt; for the handheld use this URL: https://www.123inkt.nl/123inkt-kassarol-57x40x12-thermo-wit-5-stuks-40349EC-K-6023C-i52200-t4160297.html',
    },
    '55': {
      question: 'Does Bistroo replace a broken printer?',
      answer:
        'Yes, provided the Bistroo equipment has been handled with care, we will replace it under normal circumstances.',
    },
    '56': {
      question: 'Can I change the language on the handheld?',
      answer:
        'Yes, via the device settings. However, Dutch or English is recommended for Bistroo software.',
    },
    '57': {
      question: 'Where can I buy receipt rolls for the Bistroo handheld?',
      answer:
        'You can do so via 123inkt, using this link: https://www.123inkt.nl/123inkt-kassarol-57x40x12-thermo-wit-5-stuks-40349EC-K-6023C-i52200-t4160297.html',
    },
    '58': {
      question: 'Which ordering kiosks does Bistroo provide?',
      answer:
        'We offer an entry-level model from Sunmi, but can also provide custom models via our partners — contact us for the options.',
    },
    '59': {
      question: 'I already have hardware; can Bistroo use it?',
      answer:
        'Generally yes, however we need a clear photo or model number to confirm our software can run properly on the existing hardware. In principle our rule is that we can run on everything.',
    },
    '60': {
      question: 'I want to close my shop on Bistroo; how do I do that?',
      answer:
        'Set the online shop to closed in the Bistroo Manager or on the handheld via the toggle switch. To close remotely, go to merchant.bistroo.nl.',
    },
    '61': {
      question: 'How do I open my shop on Bistroo on an extra day?',
      answer:
        "Open your shop via the toggle in the Bistroo Manager or set alternative opening hours under 'opening hours & service' if you are deviating from your standard opening hours.",
    },
    '62': {
      question: 'Do I have to manually reopen my shop on Bistroo after closing?',
      answer:
        'No. Once the set date has passed, the Bistroo Manager automatically switches back to your regular weekly schedule.',
    },
    '63': {
      question: 'Can I schedule a full vacation period in Bistroo software?',
      answer:
        'Yes. You can add multiple entries to the alternative opening hours. For each vacation day, simply add a new entry in the Bistroo Manager.',
    },
    '64': {
      question: 'How do I adjust delivery times in Bistroo software?',
      answer:
        "Via the Bistroo Manager > Branch Settings > 'opening hours & service' > opening hours or alternative times per distribution channel (pickup, delivery, on-site).",
    },
    '65': {
      question: 'How do I adjust my prices in Bistroo?',
      answer:
        'Go to Products & Inventory in the Bistroo Manager and click on the product to adjust the price. You can search for a product by typing in the search field.',
    },
    '66': {
      question: 'How do I update my menu in Bistroo software?',
      answer:
        'Adjust products or categories in the Bistroo Manager. You can also create a new menu and link it to the Bistroo software.',
    },
    '67': {
      question: 'How do I temporarily hide products in Bistroo software?',
      answer:
        'Temporarily disable the product in the Bistroo Manager using the toggle next to the product. Or click on menu in the Bistroo handheld and use the toggles per product there.',
    },
    '68': {
      question: 'How do I set up delivery areas in Bistroo software?',
      answer:
        "Go to 'opening hours & service' > Delivery in the Bistroo Manager and add or adjust areas there.",
    },
    '69': {
      question: 'How do I change delivery costs in Bistroo software?',
      answer:
        "Via 'opening hours & service' > Delivery in the Bistroo Manager, adjust the delivery costs per area.",
    },
    '70': {
      question: 'I want to offer free delivery via Bistroo software; how do I set that up?',
      answer:
        'Set the delivery costs to 0 for the desired postal code area in the Bistroo Manager settings.',
    },
    '71': {
      question: 'Can I offer a lunch and dinner menu simultaneously via Bistroo software?',
      answer:
        'Yes, you can create multiple menus and activate them based on time via Bistroo software.',
    },
    '72': {
      question: 'Can I offer a menu only during a certain time?',
      answer:
        'Yes, configure in the Bistroo Manager when the menu is available (for example, only during lunch or dinner).',
    },
    '73': {
      question: 'Can I offer products only during certain times?',
      answer: 'Yes, via the Bistroo Manager you can set time slots per category.',
    },
    '74': {
      question: "Why does 'no drink' appear on the Bistroo receipt?",
      answer:
        "This happens when a product has a selection menu (dropdown) where 'no drink' is set as the default value in the Bistroo Manager.",
    },
    '75': {
      question: "Is it necessary for 'no drink' to appear on the Bistroo receipt?",
      answer:
        "No. In most cases this is unnecessary. You can set in the Bistroo Manager that the first value is optional, so it will not be printed.",
    },
    '76': {
      question: 'When will I receive my payout from Bistroo?',
      answer:
        'Payouts from Bistroo are linked to the schedule of payment provider Stripe. Revenue for online orders is calculated from Thursday through Wednesday by default, and paid out on Monday. The cycle is different for card (PIN) payments.',
    },
    '77': {
      question: 'The Bistroo payout is incorrect; how can that be?',
      answer:
        'Check the turnover invoices in the Bistroo Manager. The Bistroo deduction is clearly stated there.',
    },
    '78': {
      question: 'How do I check my payouts in Bistroo software?',
      answer:
        'Log in to the Bistroo Manager via merchant.bistroo.nl > Invoices. Here you can see exactly what has been paid out by Bistroo per transaction.',
    },
    '79': {
      question: 'I received too little money from Bistroo; how can this be?',
      answer:
        "Log in to the Bistroo Manager via merchant.bistroo.nl and look under the invoices section, then open the most recent invoice. You will see which transactions were included in the payout. Remember that Stripe's standard weekly cycle for online payments runs from Thursday to Wednesday, and is paid out on Monday. Also check whether you processed any refunds during the period, as this is also clearly stated on the turnover invoice.",
    },
    '80': {
      question: 'What other costs are deducted by Bistroo?',
      answer:
        'Stripe transaction costs, possible bank card fees and, if applicable, hardware installments or corrections are deducted from the Bistroo payout.',
    },
    '81': {
      question: 'How do I receive my Bistroo invoices?',
      answer:
        'Invoices can be found in the Bistroo Manager under the Invoicing section. You will also receive them weekly from Bistroo at your email address.',
    },
    '82': {
      question: 'How do I download my Bistroo invoices?',
      answer:
        'Go to Invoices in the Bistroo Manager, select the period and download as a PDF and Excel file.',
    },
    '83': {
      question: 'How do I find my daily revenue at Bistroo?',
      answer:
        'You can find your daily revenue in the Bistroo Manager on the dashboard or by printing reports from the Bistroo handheld.',
    },
    '84': {
      question: 'How do I print my daily revenue via Bistroo?',
      answer:
        'Via the reporting section in the Bistroo Manager, you can export and print daily reports. On the Bistroo handheld, click the icon in the top left of the screen.',
    },
    '85': {
      question: "What should I do if I made a mistake in a customer's order?",
      answer:
        'As a restaurant owner, you can open the order and process a (partial) refund via the Bistroo handheld or the Bistroo Manager.',
    },
    '86': {
      question: 'How do I cancel an order in Bistroo software?',
      answer:
        'Open the order in the Bistroo Manager and choose refund. This cancels the order.',
    },
    '87': {
      question: 'How do I resolve a complaint using Bistroo software?',
      answer:
        'Resolve the complaint directly with the customer and offer a discount or refund if appropriate via Bistroo software.',
    },
    '88': {
      question: 'Does Bistroo resolve complaints?',
      answer:
        'Bistroo assists with complaints, but the restaurant owner remains responsible for the service, the quality of the food and the delivery.',
    },
    '90': {
      question: "What if a customer doesn't answer when receiving a Bistroo delivery?",
      answer:
        'Try to contact them by phone first. Orders via Bistroo are always prepaid, so you do not run a direct financial risk.',
    },
    '91': {
      question: 'I want to set up a promotion day; does Bistroo software offer that option?',
      answer:
        'Use the discount module in the Bistroo Manager to schedule a discount promotion on specific days or for a certain period.',
    },
    '92': {
      question: 'Does Bistroo help me with advertising?',
      answer:
        'Yes, Bistroo offers 1,000 free flyers for a first promotion. You can also have white-label flyers printed via Bistroo.',
    },
    '94': {
      question: 'How quickly do you update my menu when I submit a change to Bistroo?',
      answer:
        'Bistroo aims to implement changes within 24–48 hours. In urgent cases, Bistroo helps faster.',
    },
    '95': {
      question: 'How do I reach Bistroo customer service?',
      answer:
        "The Bistroo team can be reached by phone or via WhatsApp at 085-0250205. Our email address is klantenservice{'@'}bistroo.nl.",
    },
    '96': {
      question: 'How can I test whether my changes in Bistroo software were successful?',
      answer:
        'Contact Bistroo customer service to have the online environment temporarily set to test mode.',
    },
  },
}
